import { useCallback, useEffect, useRef, useState } from 'react';
import type { StudyEventInput, StudyRecordLinkInput } from '@common/domain/study-telemetry';
import type { LearningModality } from '@common/domain/learning-workspace';
import { resolveStudyParticipantId, StudyTelemetryClient } from '../../lib/analytics/studyTelemetry';
import { StudyEngagementClock, type EngagementSnapshot, type EngagementSurface } from './studyEngagement';

type ProgressMilestone = 25 | 50 | 75 | 100;
type InteractiveSurface = Exclude<EngagementSurface, 'inactive'>;
type EventContextKey = 'eventId' | 'studySessionId' | 'occurredAt' | 'personaId' | 'personaSlug'
  | 'modality' | 'chapterSlug' | 'viewport' | 'layout';
type StudyEventVariant = StudyEventInput extends infer Event
  ? Event extends StudyEventInput ? Omit<Event, EventContextKey> : never
  : never;

export interface StudyWorkspaceLayoutState {
  chapterSlug: string;
  materialVisible: boolean;
  materialPercent: number;
  narrowMode: boolean;
  narrowSurface: InteractiveSurface;
}
interface CurrentStudyContext extends StudyWorkspaceLayoutState {
  personaId?: string;
  personaSlug: string;
  modality: LearningModality;
}
interface StudySession {
  participantId?: string;
  sessionId: string;
  started: boolean;
  ended: boolean;
  clock: StudyEngagementClock;
  lastSurface: EngagementSurface;
  milestonesByChapter: Map<string, Set<ProgressMilestone>>;
  recordLinks: Map<string, StudyRecordLinkInput>;
}
interface ClientConfig { key: string; accessToken: string; participantId: string }
interface ClientOwner { key: string; client: StudyTelemetryClient }

export interface StudyWorkspaceTelemetryInput extends StudyWorkspaceLayoutState {
  accessToken?: string;
  ready: boolean;
  personaId?: string;
  personaSlug: string;
  modality: LearningModality;
}
export interface StudyWorkspaceTelemetry {
  studySessionId?: string;
  noteMaterialInteraction: () => void;
  noteTeachingInteraction: () => void;
  syncLayout: (layout: StudyWorkspaceLayoutState) => void;
  noteMaterialVisibility: (visible: boolean, nextLayout?: StudyWorkspaceLayoutState) => void;
  noteMaterialSelection: (chapterSlug: string) => void;
  noteMaterialProgress: (milestone: ProgressMilestone) => void;
  noteDividerResize: (
    materialPercent: number,
    direction?: 'material_expanded' | 'teaching_expanded',
  ) => void;
  noteTranscriptVisibility: (visible: boolean) => void;
  linkRecord: (
    recordId: string,
    origin: Pick<StudyRecordLinkInput, 'personaId' | 'modality'>,
  ) => Promise<boolean>;
}

function nowMonotonic(): number { return performance.now(); }
function createUuid(): string {
  const browserCrypto = globalThis.crypto;
  if (typeof browserCrypto?.randomUUID === 'function') return browserCrypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof browserCrypto?.getRandomValues === 'function') browserCrypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function safeAsyncCall(operation: () => Promise<unknown>): void {
  try { void operation().catch(() => undefined); } catch { /* non-blocking */ }
}
function resolveStudyStorage(): Storage {
  try { return window.localStorage; } catch {
    const values = new Map<string, string>();
    return {
      get length() { return values.size; },
      clear: () => values.clear(),
      getItem: (key) => values.get(key) ?? null,
      key: (index) => Array.from(values.keys())[index] ?? null,
      removeItem: (key) => { values.delete(key); },
      setItem: (key, value) => { values.set(key, value); },
    };
  }
}
function durationDetails(snapshot: EngagementSnapshot) {
  return {
    materialVisibleMs: Math.round(snapshot.materialVisibleMs),
    materialActiveMs: Math.round(snapshot.materialActiveMs),
    teachingActiveMs: Math.round(snapshot.teachingActiveMs),
  };
}
function layoutFor(state: StudyWorkspaceLayoutState) {
  if (!state.materialVisible) return { materialPercent: 0, teachingPercent: 100 };
  if (state.narrowMode) return state.narrowSurface === 'material'
    ? { materialPercent: 100, teachingPercent: 0 }
    : { materialPercent: 0, teachingPercent: 100 };
  return { materialPercent: state.materialPercent, teachingPercent: 100 - state.materialPercent };
}
function createStudyEvent(
  session: StudySession,
  current: CurrentStudyContext,
  variant: StudyEventVariant,
): StudyEventInput {
  if (!current.personaId) throw new Error('Study persona is unavailable');
  return {
    eventId: createUuid(), studySessionId: session.sessionId, occurredAt: new Date().toISOString(),
    personaId: current.personaId, personaSlug: current.personaSlug, modality: current.modality,
    chapterSlug: current.chapterSlug,
    viewport: { width: Math.max(1, Math.round(window.innerWidth)), height: Math.max(1, Math.round(window.innerHeight)) },
    layout: layoutFor(current), ...variant,
  } as StudyEventInput;
}

function createStudySession(participantId?: string): StudySession {
  const startedAt = nowMonotonic();
  const session: StudySession = {
    participantId,
    sessionId: createUuid(),
    started: false,
    ended: false,
    clock: new StudyEngagementClock(startedAt),
    lastSurface: 'inactive',
    milestonesByChapter: new Map(),
    recordLinks: new Map(),
  };
  session.clock.setDocumentVisible(!document.hidden, startedAt);
  return session;
}

export function useStudyWorkspaceTelemetry(input: StudyWorkspaceTelemetryInput): StudyWorkspaceTelemetry {
  const currentRef = useRef<CurrentStudyContext>({ ...input });
  currentRef.current = {
    chapterSlug: input.chapterSlug, materialVisible: input.materialVisible,
    materialPercent: input.materialPercent, narrowMode: input.narrowMode, narrowSurface: input.narrowSurface,
    personaId: input.personaId, personaSlug: input.personaSlug, modality: input.modality,
  };
  const resolvedParticipantId = resolveStudyParticipantId(input.accessToken);
  const sessionRef = useRef<StudySession>();
  if (!sessionRef.current) sessionRef.current = createStudySession(resolvedParticipantId);
  const ownerRef = useRef<ClientOwner>();
  const desiredConfigRef = useRef<ClientConfig>();
  const transitionRef = useRef<Promise<void>>();
  const stoppedClientsRef = useRef(new WeakSet<StudyTelemetryClient>());
  const terminalRef = useRef(false);
  const mountGenerationRef = useRef(0);
  const [, setClientEpoch] = useState(0);
  const clientConfig = input.ready && input.accessToken && resolvedParticipantId && input.personaId
    ? { key: `${resolvedParticipantId}\u0000${input.accessToken}`, accessToken: input.accessToken, participantId: resolvedParticipantId }
    : undefined;
  desiredConfigRef.current = clientConfig;

  const stopClient = useCallback((client: StudyTelemetryClient): Promise<void> => {
    if (stoppedClientsRef.current.has(client)) return Promise.resolve();
    stoppedClientsRef.current.add(client);
    try { return client.stop(); } catch { return Promise.resolve(); }
  }, []);

  const enqueue = useCallback((variant: StudyEventVariant) => {
    const session = sessionRef.current;
    const owner = ownerRef.current;
    if (!session || !owner || session.ended || !currentRef.current.personaId) return;
    try { owner.client.enqueue(createStudyEvent(session, currentRef.current, variant)); } catch { /* non-blocking */ }
  }, []);
  const emitSurfaceIfChanged = useCallback((snapshot: EngagementSnapshot) => {
    const session = sessionRef.current;
    if (!session || snapshot.activeSurface === session.lastSurface) return;
    session.lastSurface = snapshot.activeSurface;
    enqueue({ eventName: 'active_surface_changed', details: { surface: snapshot.activeSurface } });
  }, [enqueue]);
  const endSession = useCallback(() => {
    const session = sessionRef.current;
    const owner = ownerRef.current;
    if (!session || !session.started || session.ended) return;
    const snapshot = session.clock.tick(nowMonotonic());
    session.ended = true;
    if (!owner || !currentRef.current.personaId) return;
    try {
      owner.client.enqueue(createStudyEvent(session, currentRef.current, {
        eventName: 'study_session_ended', details: durationDetails(snapshot),
      }));
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const reconcileClient = async () => {
      const desired = desiredConfigRef.current;
      const existing = ownerRef.current;
      if (existing?.key === desired?.key || terminalRef.current) return;
      const currentSession = sessionRef.current;
      const participantChanged = Boolean(
        desired?.participantId
        && currentSession?.participantId
        && desired.participantId !== currentSession.participantId,
      );
      const becomingIneligible = Boolean(!desired && existing && currentSession?.started && !currentSession.ended);
      if (participantChanged || becomingIneligible) endSession();
      if (existing) {
        ownerRef.current = undefined;
        setClientEpoch((value) => value + 1);
        try { await stopClient(existing.client); } catch { /* replacement must continue */ }
      }
      const latest = desiredConfigRef.current;
      if (cancelled || !latest || latest.key !== desired?.key || terminalRef.current) return;
      if (participantChanged || sessionRef.current?.ended || !sessionRef.current?.participantId) {
        sessionRef.current = createStudySession(latest.participantId);
      }
      let owner: ClientOwner;
      try {
        owner = { key: latest.key, client: new StudyTelemetryClient({
          accessToken: latest.accessToken, storage: resolveStudyStorage(),
        }) };
      } catch { return; }
      ownerRef.current = owner;
      const session = sessionRef.current;
      if (session && !session.started) {
        session.started = true;
        enqueue({ eventName: 'study_session_started' });
      }
      session?.recordLinks.forEach((recordLink) => safeAsyncCall(
        () => owner.client.linkRecord(session.sessionId, recordLink),
      ));
      setClientEpoch((value) => value + 1);
    };
    const previous = transitionRef.current;
    const transition = previous ? previous.catch(() => undefined).then(reconcileClient) : reconcileClient();
    const tracked = transition.finally(() => {
      if (transitionRef.current === tracked) transitionRef.current = undefined;
    });
    transitionRef.current = tracked;
    return () => { cancelled = true; };
  }, [clientConfig?.key, endSession, enqueue, stopClient]);

  useEffect(() => {
    mountGenerationRef.current += 1;
    const generation = mountGenerationRef.current;
    let windowFocused = true;
    const updateVisibility = () => {
      const session = sessionRef.current;
      const owner = ownerRef.current;
      if (!session || !session.started || session.ended || !owner) return;
      const now = nowMonotonic();
      session.clock.setDocumentVisible(!document.hidden && windowFocused, now);
      emitSurfaceIfChanged(session.clock.tick(now));
      safeAsyncCall(() => owner.client.flush());
    };
    const handleFocus = () => { windowFocused = true; updateVisibility(); };
    const handleBlur = () => { windowFocused = false; updateVisibility(); };
    const handlePageHide = () => {
      terminalRef.current = true;
      endSession();
      const owner = ownerRef.current;
      if (owner) safeAsyncCall(() => stopClient(owner.client));
    };
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      queueMicrotask(() => {
        if (mountGenerationRef.current !== generation) return;
        terminalRef.current = true;
        endSession();
        const owner = ownerRef.current;
        if (owner) safeAsyncCall(() => stopClient(owner.client));
      });
    };
  }, [emitSurfaceIfChanged, endSession, stopClient]);

  const noteInteraction = useCallback((surface: InteractiveSurface) => {
    const session = sessionRef.current;
    if (!session?.started || session.ended || !ownerRef.current) return;
    const now = nowMonotonic();
    if (surface === 'material') session.clock.noteMaterialInteraction(now);
    else session.clock.noteTeachingInteraction(now);
    emitSurfaceIfChanged(session.clock.tick(now));
  }, [emitSurfaceIfChanged]);
  const syncLayout = useCallback((layout: StudyWorkspaceLayoutState) => {
    Object.assign(currentRef.current, layout);
  }, []);
  const noteMaterialVisibility = useCallback((visible: boolean, nextLayout?: StudyWorkspaceLayoutState) => {
    const session = sessionRef.current;
    const owner = ownerRef.current;
    if (!session?.started || session.ended || !owner) return;
    const now = nowMonotonic();
    session.clock.setMaterialVisible(visible, now);
    if (nextLayout) Object.assign(currentRef.current, nextLayout);
    currentRef.current.materialVisible = visible;
    enqueue({ eventName: 'material_visibility_changed', details: { visible } });
    emitSurfaceIfChanged(session.clock.tick(now));
    safeAsyncCall(() => owner.client.flush());
  }, [emitSurfaceIfChanged, enqueue]);
  const noteMaterialSelection = useCallback((chapterSlug: string) => {
    const session = sessionRef.current;
    if (!session?.started || session.ended || !ownerRef.current) return;
    currentRef.current.chapterSlug = chapterSlug;
    enqueue({ eventName: 'material_selected', details: { chapterSlug } });
  }, [enqueue]);
  const noteMaterialProgress = useCallback((milestone: ProgressMilestone) => {
    const session = sessionRef.current;
    if (!session?.started || session.ended || !ownerRef.current) return;
    let milestones = session.milestonesByChapter.get(currentRef.current.chapterSlug);
    if (!milestones) {
      milestones = new Set();
      session.milestonesByChapter.set(currentRef.current.chapterSlug, milestones);
    }
    if (milestones.has(milestone)) return;
    milestones.add(milestone);
    enqueue({ eventName: 'material_progress_changed', details: {
      ...durationDetails(session.clock.tick(nowMonotonic())), milestone,
    } });
  }, [enqueue]);
  const noteDividerResize = useCallback((
    materialPercent: number,
    direction?: 'material_expanded' | 'teaching_expanded',
  ) => {
    const session = sessionRef.current;
    if (!session?.started || session.ended || !ownerRef.current) return;
    const previous = currentRef.current.materialPercent;
    if (!direction && Math.abs(materialPercent - previous) < 0.001) return;
    currentRef.current.materialPercent = materialPercent;
    enqueue({ eventName: 'divider_resized', details: {
      materialPercent, teachingPercent: 100 - materialPercent,
      direction: direction ?? (materialPercent >= previous ? 'material_expanded' : 'teaching_expanded'),
    } });
  }, [enqueue]);
  const noteTranscriptVisibility = useCallback((visible: boolean) => {
    const session = sessionRef.current;
    if (!session?.started || session.ended || !ownerRef.current) return;
    enqueue({ eventName: 'transcript_visibility_changed', details: { visible } });
  }, [enqueue]);
  const linkRecord = useCallback(async (
    recordId: string,
    origin: Pick<StudyRecordLinkInput, 'personaId' | 'modality'>,
  ): Promise<boolean> => {
    const session = sessionRef.current;
    if (!session?.participantId || session.ended) return false;
    const recordLink: StudyRecordLinkInput = {
      personaId: origin.personaId, modality: origin.modality, recordId,
    };
    session.recordLinks.set(`${recordLink.modality}:${recordId}`, recordLink);
    const owner = ownerRef.current;
    if (!session.started || !owner) return true;
    try {
      await owner.client.linkRecord(session.sessionId, recordLink);
      return true;
    } catch {
      return false;
    }
  }, []);

  const session = sessionRef.current;
  return {
    studySessionId: session?.started && !session.ended ? session.sessionId : undefined,
    noteMaterialInteraction: useCallback(() => noteInteraction('material'), [noteInteraction]),
    noteTeachingInteraction: useCallback(() => noteInteraction('teaching'), [noteInteraction]),
    syncLayout, noteMaterialVisibility, noteMaterialSelection, noteMaterialProgress, noteDividerResize,
    noteTranscriptVisibility, linkRecord,
  };
}
